import React, { useState } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { useAuth } from '../../lib/auth';
import { updateProfile } from '../../lib/api';
import { SectionShell, SettingsCard } from './_shared';

const APPEARANCE = [
  { key: 'light', profile: 'light', theme: 'light', label: 'Light', icon: Sun },
  { key: 'dark', profile: 'dark', theme: 'dark', label: 'Dark', icon: Moon },
  { key: 'system', profile: 'system', theme: 'auto', label: 'System', icon: Monitor },
];

const TIMEZONES = [
  { v: 'America/St_Johns', label: 'Newfoundland (NDT)' },
  { v: 'America/Halifax', label: 'Atlantic (ADT)' },
  { v: 'America/Toronto', label: 'Eastern (EDT)' },
  { v: 'America/Winnipeg', label: 'Central (CDT)' },
  { v: 'America/Regina', label: 'Central Standard (SK)' },
  { v: 'America/Edmonton', label: 'Mountain (MDT)' },
  { v: 'America/Vancouver', label: 'Pacific (PDT)' },
  { v: 'America/Whitehorse', label: 'Yukon (MST)' },
  { v: 'UTC', label: 'UTC' },
];

export default function PreferencesSection({ onToast }) {
  const { colors, mode, setMode } = useTheme();
  const { user, updateUser } = useAuth();
  const [tz, setTz] = useState(user?.timezone || 'America/Toronto');
  const [lang, setLang] = useState(user?.language || 'en-CA');
  const [busy, setBusy] = useState('');

  const setAppearance = async (opt) => {
    setMode(opt.theme);               // immediate visual effect
    setBusy('appearance');
    try {
      const updated = await updateProfile({ appearance: opt.profile });
      updateUser(updated);
      onToast?.('Appearance updated');
    } catch (e) {
      onToast?.(e?.detail || 'Could not persist appearance');
    } finally {
      setBusy('');
    }
  };

  const setLanguage = async (next) => {
    if (next === lang) return;
    setLang(next);
    setBusy('language');
    try {
      const updated = await updateProfile({ language: next });
      updateUser(updated);
      onToast?.('Language updated');
    } catch (e) {
      onToast?.(e?.detail || 'Could not update language');
      setLang(user?.language || 'en-CA');
    } finally {
      setBusy('');
    }
  };

  const setTimezone = async (next) => {
    if (next === tz) return;
    setTz(next);
    setBusy('timezone');
    try {
      const updated = await updateProfile({ timezone: next });
      updateUser(updated);
      onToast?.('Timezone updated');
    } catch (e) {
      onToast?.(e?.detail || 'Could not update timezone');
      setTz(user?.timezone || 'America/Toronto');
    } finally {
      setBusy('');
    }
  };

  const currentAppearance = APPEARANCE.find((a) => a.theme === mode) || APPEARANCE[2];

  return (
    <SectionShell eyebrow="Preferences" title="Preferences" description="How the app looks and talks to you." testid="section-preferences">
      <SettingsCard title="Appearance" description="Choose a theme or let the app follow your system." testid="pref-appearance">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {APPEARANCE.map((opt) => {
            const Icon = opt.icon;
            const active = currentAppearance.key === opt.key;
            return (
              <button
                key={opt.key} type="button" onClick={() => setAppearance(opt)}
                data-testid={`appearance-${opt.key}`}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  padding: '14px 10px', borderRadius: 12,
                  border: `1px solid ${active ? colors.accent : colors.border}`,
                  background: active ? colors.accentSoft : colors.surface,
                  color: active ? colors.accent : colors.text,
                  cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: 13,
                  position: 'relative',
                }}
              >
                <Icon size={18} />
                {opt.label}
                {active && <Check size={14} style={{ position: 'absolute', top: 8, right: 8 }} />}
              </button>
            );
          })}
        </div>
      </SettingsCard>

      <SettingsCard title="Language" description="English only for now. French (CA) is coming soon." testid="pref-language">
        <select
          value={lang} onChange={(e) => setLanguage(e.target.value)}
          disabled={busy === 'language'}
          data-testid="language-select"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: 14 }}
        >
          <option value="en-CA">English (CA)</option>
          <option value="fr-CA" disabled>Français (CA) — coming soon</option>
        </select>
      </SettingsCard>

      <SettingsCard title="Timezone" description="Used for date formatting + CRA deadline reminders." testid="pref-timezone">
        <select
          value={tz} onChange={(e) => setTimezone(e.target.value)}
          disabled={busy === 'timezone'}
          data-testid="timezone-select"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: 14 }}
        >
          {TIMEZONES.map((o) => <option key={o.v} value={o.v}>{o.label} — {o.v}</option>)}
        </select>
      </SettingsCard>
    </SectionShell>
  );
}
